import React, { useState } from 'react';
import { ArrowLeft, HelpCircle, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const FAQ = () => {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'ar'>('en');

  const faqs = [
    {
      en: {
        question: "What is CrossLink?",
        answer: "CrossLink is a Kuwait-based tech company dedicated to making people's lives easier by offering simple website building services and medical AI prompts for individuals and businesses."
      },
      ar: {
        question: "ما هي [ كروس لينك ]؟",
        answer: "[ كروس لينك ] هي شركة تقنية كويتية تهدف إلى تسهيل حياة الناس من خلال تقديم خدمات بناء مواقع إلكترونية وقوالب ذكاء اصطناعي طبية للأفراد والشركات."
      }
    },
    {
      en: {
        question: "What products do you offer?",
        answer: "Website Building: Professional websites built for your business.\nMedical AI Prompts: Specialized prompts for medical professionals and students."
      },
      ar: {
        question: "ما هي المنتجات التي تقدمونها؟",
        answer: "خدمة بناء المواقع الإلكترونية: بناء مواقع إلكترونية احترافية لعملك.\nقوالب الذكاء الاصطناعي الطبية: قوالب متخصصة للمتخصصين الطبيين والطلاب."
      }
    },
    {
      en: {
        question: "How long does it take to get my website?",
        answer: "Website build time is usually 7 - 35 days, depending on the package chosen and your requirements."
      },
      ar: {
        question: "كم يستغرق بناء الموقع الإلكتروني؟",
        answer: "مدة البناء من ٧ إلى ٣٥ يوم عمل حسب الباقة المختارة واحتياجك."
      }
    },
    {
      en: {
        question: "How do I order?",
        answer: "Fill out our consultation form or questionnaire\nContact us on Instagram or WhatsApp"
      },
      ar: {
        question: "كيف يمكنني الطلب؟",
        answer: "قم بتعبئة نموذج الاستشارة أو الاستبيان\nأو تواصل معنا عبر إنستغرام أو واتساب"
      }
    },
    {
      en: {
        question: "What if I have a problem with my website?",
        answer: "Reach out directly via WhatsApp or Instagram. We provide ongoing support and monthly check-ins."
      },
      ar: {
        question: "ماذا أفعل إذا واجهت مشكلة في الموقع؟",
        answer: "تواصل معنا مباشرة عبر واتساب أو إنستغرام. نحن نوفر دعم مستمر ونتابع مع العملاء شهرياً لضمان رضاهم."
      }
    },
    {
      en: {
        question: "Why should I choose CrossLink?",
        answer: "We offer professional website building services with expert design, ongoing support, and transparent pricing tailored to your needs."
      },
      ar: {
        question: "لماذا أختار [ كروس لينك ]؟",
        answer: "نقدم خدمات بناء مواقع إلكترونية احترافية مع تصميم متخصص ودعم مستمر وأسعار شفافة مخصصة لاحتياجاتك."
      }
    },
    {
      en: {
        question: "Who are your typical customers?",
        answer: "Small and medium businesses, clinics, doctors, students, professionals, and anyone who wants a professional online presence."
      },
      ar: {
        question: "من هم عملاؤكم المستهدفون؟",
        answer: "الشركات الصغيرة والمتوسطة، العيادات، الأطباء، الطلاب، المتخصصون، وكل من يرغب في وجود احترافي على الإنترنت."
      }
    }
  ];

  const toggleLanguage = () => {
    setCurrentLanguage(currentLanguage === 'en' ? 'ar' : 'en');
  };

  const isArabic = currentLanguage === 'ar';

  return (
    <div className={`min-h-screen bg-black text-white py-12 px-4 ${isArabic ? 'rtl' : 'ltr'}`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className={`flex items-center mb-8 ${isArabic ? 'flex-row-reverse' : ''}`}>
          <Link 
            to="/" 
            className={`flex items-center text-gray-400 hover:text-white transition-colors duration-300 ${isArabic ? 'ml-4' : 'mr-4'}`}
          >
            <ArrowLeft className={`w-5 h-5 ${isArabic ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {isArabic ? 'العودة للرئيسية' : 'Back to Home'}
          </Link>
        </div>

        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <HelpCircle className="w-12 h-12 text-green-500 mr-3" />
            <h1 className="text-4xl font-bold">
              {isArabic ? 'الأسئلة الشائعة – [ كروس لينك ]' : 'CrossLink FAQ'}
            </h1>
          </div>
          <p className="text-gray-400 text-lg mb-6">
            {isArabic ? 'كل ما تحتاج لمعرفته عن خدماتنا' : 'Everything you need to know about our services'}
          </p>
          
          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center mx-auto bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-300"
          >
            <Globe className="w-5 h-5 mr-2" />
            {isArabic ? 'English' : 'العربية'}
          </button>
        </div>

        {/* FAQ Items */}
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="bg-gray-900 rounded-xl p-6 hover:bg-gray-800 transition-colors duration-300"
            >
              <h3 className={`text-xl font-semibold text-green-400 mb-3 flex items-start ${isArabic ? 'flex-row-reverse text-right' : ''}`}>
                <span className={`bg-green-500 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mt-0.5 flex-shrink-0 ${isArabic ? 'ml-3' : 'mr-3'}`}>
                  {isArabic ? (index + 1).toLocaleString('ar-EG') : index + 1}
                </span>
                {faq[currentLanguage].question}
              </h3>
              <div className={`text-gray-300 leading-relaxed ${isArabic ? 'mr-9 text-right' : 'ml-9'}`}>
                {faq[currentLanguage].answer.split('\n').map((line, lineIndex) => (
                  <p key={lineIndex} className={lineIndex > 0 ? 'mt-2' : ''}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-green-900 to-green-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4">
            {isArabic ? 'لا تزال لديك أسئلة؟' : 'Still have questions?'}
          </h2>
          <p className="text-gray-300 mb-6">
            {isArabic ? 'لا تتردد في التواصل معنا مباشرة!' : "Don't hesitate to reach out to us directly!"}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/96598551104"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
            >
              {isArabic ? 'واتساب' : 'WhatsApp Us'}
            </a>
            <a
              href="https://instagram.com/cross_link.kw"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-300"
            >
              {isArabic ? 'رسالة إنستغرام' : 'Instagram DM'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;